import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient, CLIENT_SESSION_ID } from "./apiClient";
import { useAuth } from "../context/AuthContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

// Map a backend entity -> the TanStack Query keys it affects, so a change
// invalidates only the relevant lists/counts (not the whole cache). Mirrors the
// cross-entity invalidations already used by the Phase 1-3 mutations.
const ENTITY_KEYS = {
  customers: ["customers-list"],
  subscriptions: [
    "subscriptions-list", "subscription-counts", "subscription-paused-counts",
    "subscription-deliveries-today-count", "subscription-pickup-counts",
  ],
  payments: ["payments-list", "subscriptions-list", "subscription-counts"],
  deliveries: [
    "deliveries-list", "routes-list", "subscription-deliveries-today-count",
    "subscription-paused-counts", "dashboard-summary",
  ],
  routes: ["routes-list", "deliveries-list"],
};

const ALL_KEYS = [...new Set(Object.values(ENTITY_KEYS).flat())];

// Opens an authenticated SSE stream and invalidates affected queries when
// another admin changes data, so open lists/counts update within ~1-2s without
// a manual refresh. Invalidation keeps current filters/pagination/scroll (only
// active queries refetch, with keepPreviousData). Skips events caused by the
// current user (their own mutation already updated the cache locally).
export function useRealtime() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const lastIdRef = useRef(null);

  useEffect(() => {
    if (!user) return undefined;
    let es = null;
    let stopped = false;
    let retryTimer = null;

    const connect = async () => {
      if (stopped) return;
      try {
        const { data } = await apiClient.post("/realtime/ticket");
        let url = `${API_BASE}/realtime/stream?ticket=${encodeURIComponent(data.ticket)}`;
        if (lastIdRef.current) url += `&last_event_id=${lastIdRef.current}`;
        es = new EventSource(url);

        es.addEventListener("change", (e) => {
          if (e.lastEventId) lastIdRef.current = e.lastEventId;
          let ev;
          try { ev = JSON.parse(e.data); } catch { return; }
          if (ev.by && ev.by === CLIENT_SESSION_ID) return; // skip only THIS tab's own change
          (ENTITY_KEYS[ev.entity] || []).forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
        });

        es.addEventListener("resync", () => {
          ALL_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
        });

        es.onerror = () => {
          // One-time ticket is already consumed, so stop native retry and
          // reconnect with a fresh ticket (+ Last-Event-ID for replay).
          if (es) es.close();
          if (!stopped) {
            clearTimeout(retryTimer);
            retryTimer = setTimeout(connect, 3000);
          }
        };
      } catch {
        if (!stopped) {
          clearTimeout(retryTimer);
          retryTimer = setTimeout(connect, 5000);
        }
      }
    };

    connect();
    return () => {
      stopped = true;
      if (es) es.close();
      clearTimeout(retryTimer);
    };
  }, [user, qc]);
}
